import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import WhatsAppOutlined from '@ant-design/icons/WhatsAppOutlined';
import MainCard from 'components/MainCard';
import LocationsManager from 'components/LocationsManager';
import LogoAdjustModal from 'components/LogoAdjustModal';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';

const TEMPLATES = [
  {
    id: 'clean',
    name: 'Minimal',
    desc: 'Clean white layout with light/dark toggle. Simple and professional.',
    preview: { bg: '#ffffff', accent: '#c6922b', bars: ['#f5f5f5', '#eee', '#f9f9f9'] },
  },
  {
    id: 'wellness',
    name: 'Wellness Pro',
    desc: 'Forest green split hero, serif headings, sage section alternates.',
    preview: { bg: '#eef5ee', accent: '#1e3a2f', bars: ['#1e3a2f', '#eef5ee', '#ffffff'] },
  },
  {
    id: 'impact',
    name: 'Impact',
    desc: 'Bold gradient hero, pill tag conditions, collage about, 4-col testimonials.',
    preview: { bg: '#1a1a2e', accent: '#c6922b', bars: ['#c6922b', '#f2f2f8', '#ffffff'] },
  },
  {
    id: 'modern',
    name: 'Modern',
    desc: 'Split hero with stats card, icon grid, 2-col testimonials, card footer.',
    preview: { bg: '#f7f8fc', accent: '#4f6ef7', bars: ['#eef0f8', '#ffffff', '#1a1d2e'] },
  },
];

export default function CompanySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [company, setCompany] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    contact_phone: '',
    contact_email: '',
    address: '',
    custom_domain: '',
    whatsapp_enabled: false,
    whatsapp_message: '',
    online_meeting_link: '',
    logo_fit: 'contain',
    logo_scale: 1,
    logo_position: '0% 0%'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [logoModalOpen, setLogoModalOpen] = useState(false);
  const [dominantColor, setDominantColor] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('clean');


  const tenantId = localStorage.getItem('tenant_id');
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      const response = await axios.get(endpoints.company.updateCompany(tenantId), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setCompany(data);
      setFormData({
        name: data.name || '',
        contact_phone: data.contact_phone || '',
        contact_email: data.contact_email || '',
        address: data.address || '',
        custom_domain: data.custom_domain || '',
        whatsapp_enabled: !!data.whatsapp_enabled,
        whatsapp_message: data.whatsapp_message || '',
        online_meeting_link: data.online_meeting_link || '',
        logo_fit: data.logo_fit || 'contain',
        logo_scale: data.logo_scale || 1,
        logo_position: data.logo_position || '0% 0%'
      });
      setLogoPreview(data.branding_logo || '');
      setSelectedTemplate(data.site_template || 'clean');
    } catch (err) {
      setError('Failed to load company details');
    } finally {
      setLoading(false);
    }
  };


  const extractDominantColor = (imageDataUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        const colorCount = {};
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
          if (a < 128 || (r > 240 && g > 240 && b > 240)) continue;
          const rr = Math.round(r / 10) * 10;
          const rg = Math.round(g / 10) * 10;
          const rb = Math.round(b / 10) * 10;
          const hex = `#${rr.toString(16).padStart(2, '0')}${rg.toString(16).padStart(2, '0')}${rb.toString(16).padStart(2, '0')}`;
          colorCount[hex] = (colorCount[hex] || 0) + 1;
        }
        let maxCount = 0, dominant = '#000000';
        for (const [color, count] of Object.entries(colorCount)) {
          if (count > maxCount) { maxCount = count; dominant = color; }
        }
        resolve(dominant);
      };
      img.onerror = () => resolve('#000000');
      img.src = imageDataUrl;
    });
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      const reader = new FileReader();
      reader.onload = async (event) => {
        const color = await extractDominantColor(event.target.result);
        setDominantColor(color);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('contact_phone', formData.contact_phone);
      payload.append('contact_email', formData.contact_email);
      payload.append('address', formData.address);
      // Always send custom_domain (including empty string to clear it)
      payload.append('custom_domain', formData.custom_domain || '');
      payload.append('whatsapp_enabled', formData.whatsapp_enabled ? 'true' : 'false');
      payload.append('whatsapp_message', formData.whatsapp_message || '');
      payload.append('online_meeting_link', formData.online_meeting_link || '');
      payload.append('logo_fit', formData.logo_fit || 'contain');
      payload.append('logo_scale', formData.logo_scale || 1);
      payload.append('logo_position', formData.logo_position || 'center');
      if (logoFile) {
        payload.append('branding_logo', logoFile);
      }
      if (dominantColor) {
        payload.append('branding_colour', dominantColor);
      }
      payload.append('site_template', selectedTemplate);
      await axios.patch(endpoints.company.updateCompany(tenantId), payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Company settings updated successfully');
      setLogoFile(null);
      fetchCompany();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update company settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container rowSpacing={4.5} columnSpacing={2.75}>
      <Grid sx={{ mb: -2.25 }} size={12}>
        <Typography variant="h5">Company Settings</Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <MainCard title="Company Details">
          <Stack spacing={2.5}>
            {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

            <TextField
              fullWidth
              label="Company Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              size="small"
            />
            <TextField
              fullWidth
              label="Contact Email"
              name="contact_email"
              type="email"
              value={formData.contact_email}
              onChange={handleInputChange}
              size="small"
            />
            <TextField
              fullWidth
              label="Contact Phone"
              name="contact_phone"
              value={formData.contact_phone}
              onChange={handleInputChange}
              size="small"
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              size="small"
              multiline
              rows={3}
            />

            <TextField
              fullWidth
              label="Custom Domain"
              name="custom_domain"
              placeholder="yourclinic.com (leave blank to use default)"
              value={formData.custom_domain}
              onChange={(e) => setFormData({ ...formData, custom_domain: e.target.value.toLowerCase().trim().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '') })}
              size="small"
              helperText={
                formData.custom_domain
                  ? `Point this domain's A record to 52.13.105.48, then save. Your booking page will be live at https://${formData.custom_domain}`
                  : 'Optional. Make your booking page load at your own domain.'
              }
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Company Logo</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={logoPreview}
                  alt={company?.name}
                  sx={{ width: 80, height: 80 }}
                  variant="rounded"
                >
                  {company?.name?.charAt(0) || '?'}
                </Avatar>
                <Box>
                  <Button variant="outlined" component="label" size="small">
                    {logoPreview ? 'Change Logo' : 'Upload Logo'}
                    <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
                  </Button>
                  {logoFile && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
                      {logoFile.name}
                    </Typography>
                  )}
                </Box>
              </Box>

              {logoPreview && (
                <Box sx={{ mt: 2, display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  {/* Live preview of how the logo sits in the website logo container */}
                  <Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                      Preview (website logo box)
                    </Typography>
                    <Box sx={{ width: 96, height: 96, border: '1px dashed #cbd5e1', borderRadius: 2, overflow: 'hidden', bgcolor: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={logoPreview}
                        alt="logo preview"
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          transform: `translate(${(formData.logo_position || '0% 0%').replace(' ', ', ')}) scale(${formData.logo_scale || 1})`
                        }}
                      />
                    </Box>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                      Reposition or zoom the logo so it sits nicely in its container on your website.
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => setLogoModalOpen(true)}>
                      Adjust position &amp; zoom
                    </Button>
                    <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.disabled' }}>
                      offset {formData.logo_position || '0% 0%'} · {Number(formData.logo_scale).toFixed(2)}×
                    </Typography>
                  </Box>
                </Box>
              )}

              <LogoAdjustModal
                open={logoModalOpen}
                onClose={() => setLogoModalOpen(false)}
                src={logoPreview}
                scale={formData.logo_scale}
                position={formData.logo_position}
                onApply={({ scale, position }) =>
                  setFormData((p) => ({ ...p, logo_scale: scale, logo_position: position }))
                }
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Brand Color</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="input"
                  type="color"
                  value={dominantColor || company?.branding_colour || '#000000'}
                  onChange={(e) => setDominantColor(e.target.value)}
                  sx={{ width: 36, height: 36, border: '1px solid #ddd', borderRadius: 1, cursor: 'pointer', p: 0.25, bgcolor: '#fff' }}
                />
                <TextField
                  size="small"
                  value={dominantColor || company?.branding_colour || ''}
                  onChange={(e) => setDominantColor(e.target.value)}
                  placeholder="#000000"
                  sx={{ width: 130 }}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                />
                {dominantColor && dominantColor !== company?.branding_colour && (
                  <Typography variant="caption" color="text.secondary">
                    {logoFile ? '(auto-detected — editable)' : '(manual override)'}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Site Template</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1.5 }}>
                {TEMPLATES.map((tpl) => (
                  <Box
                    key={tpl.id}
                    onClick={() => setSelectedTemplate(tpl.id)}
                    sx={{
                      border: selectedTemplate === tpl.id ? '2px solid' : '1.5px solid',
                      borderColor: selectedTemplate === tpl.id ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      bgcolor: selectedTemplate === tpl.id ? 'primary.50' : 'background.paper',
                      '&:hover': { borderColor: 'primary.light' },
                    }}
                  >
                    {/* Mini preview */}
                    <Box sx={{ height: 52, borderRadius: 1, overflow: 'hidden', mb: 1, bgcolor: tpl.preview.bg, position: 'relative' }}>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 12, bgcolor: tpl.preview.bars[0], opacity: 0.9 }} />
                      <Box sx={{ position: 'absolute', top: 14, left: 8, right: 8, height: 8, bgcolor: tpl.preview.bars[1], borderRadius: 0.5 }} />
                      <Box sx={{ position: 'absolute', top: 26, left: 8, right: 24, height: 5, bgcolor: tpl.preview.bars[2], borderRadius: 0.5 }} />
                      <Box sx={{ position: 'absolute', top: 35, left: 8, width: 24, height: 10, bgcolor: tpl.preview.accent, borderRadius: 0.5 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600}>{tpl.name}</Typography>
                      {selectedTemplate === tpl.id && <Chip label="Active" size="small" color="primary" sx={{ height: 16, fontSize: '0.65rem' }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.3, display: 'block' }}>
                      {tpl.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* ── Online consultation link (used by all doctors with online enabled) ── */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>🎥 Online consultation link</Typography>
              <TextField
                fullWidth
                size="small"
                name="online_meeting_link"
                value={formData.online_meeting_link}
                onChange={handleInputChange}
                placeholder="https://meet.google.com/abc-defg-hij  •  https://zoom.us/j/123456789"
                helperText="One persistent meeting room URL used for ALL online bookings. Patients receive this link in their booking confirmation email. Enable 'Online consultation' on each doctor's profile to make it available in the booking form."
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* ── WhatsApp Consult ── */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <WhatsAppOutlined style={{ color: '#25d366' }} />
                <Typography variant="subtitle2">Consult on WhatsApp</Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.whatsapp_enabled}
                    onChange={(e) => setFormData((p) => ({ ...p, whatsapp_enabled: e.target.checked }))}
                  />
                }
                label={
                  <Typography variant="body2">
                    Show "Consult on WhatsApp" button on the public website
                  </Typography>
                }
              />
              <TextField
                fullWidth
                label="Prefilled WhatsApp message"
                name="whatsapp_message"
                value={formData.whatsapp_message}
                onChange={handleInputChange}
                disabled={!formData.whatsapp_enabled}
                size="small"
                multiline
                rows={3}
                placeholder="Hi, I'd like to consult with you."
                helperText={
                  formData.whatsapp_enabled
                    ? `Opens https://wa.me/${(formData.contact_phone || '').replace(/\\D/g, '') || '<contact_phone>'} with this prefilled message.`
                    : 'Enable the toggle to set the prefilled message.'
                }
                sx={{ mt: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
              <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 120 }}>
                {saving ? <CircularProgress size={20} /> : 'Save Changes'}
              </Button>
            </Box>
          </Stack>
        </MainCard>

        {/* ── Locations (Branches) — same component used on /website-content ── */}
        <MainCard title="Locations / Branches" sx={{ mt: 3 }}>
          <LocationsManager tenantId={tenantId} />
        </MainCard>

        <MainCard title="Change Password" sx={{ mt: 3 }}>
          <ChangePasswordCard />
        </MainCard>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <MainCard title="Preview">
          <Stack spacing={2} alignItems="center">
            <Avatar
              src={logoPreview}
              alt={company?.name}
              sx={{ width: 120, height: 120 }}
              variant="rounded"
            >
              {company?.name?.charAt(0) || '?'}
            </Avatar>
            <Typography variant="h6">{formData.name || 'Company Name'}</Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {formData.contact_email}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {formData.contact_phone}
            </Typography>
            {formData.address && (
              <Typography variant="body2" color="text.secondary" align="center">
                {formData.address}
              </Typography>
            )}
          </Stack>
        </MainCard>
      </Grid>
    </Grid>
  );
}

// ── Change Password card ────────────────────────────────────────────────────
function ChangePasswordCard() {
  const token = localStorage.getItem('token');
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async () => {
    setError(''); setSuccess('');
    if (!oldPw || !newPw) { setError('Both current and new passwords are required.'); return; }
    if (newPw.length < 8) { setError('New password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setError('New password and confirmation do not match.'); return; }
    if (newPw === oldPw) { setError('New password must differ from the current one.'); return; }
    try {
      setSaving(true);
      await axios.post(
        endpoints.auth.changePassword,
        { old_password: oldPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Password changed successfully.');
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join('. ') : 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Update the password for your admin account.
      </Typography>
      {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      <TextField
        size="small" type="password" label="Current password" autoComplete="current-password"
        value={oldPw} onChange={(e) => setOldPw(e.target.value)} fullWidth
      />
      <TextField
        size="small" type="password" label="New password" autoComplete="new-password"
        value={newPw} onChange={(e) => setNewPw(e.target.value)} fullWidth
        helperText="At least 8 characters."
      />
      <TextField
        size="small" type="password" label="Confirm new password" autoComplete="new-password"
        value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} fullWidth
        error={confirmPw !== '' && confirmPw !== newPw}
        helperText={confirmPw !== '' && confirmPw !== newPw ? 'Does not match new password' : ' '}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" onClick={submit} disabled={saving} sx={{ minWidth: 150 }}>
          {saving ? <CircularProgress size={20} /> : 'Change Password'}
        </Button>
      </Box>
    </Stack>
  );
}
